import type {
	FormField,
	FormSchema,
	RequestTypeVersionFullDTO,
	WorkflowConfig,
	WorkflowStepConfig,
} from '@kfolks/types'
import { canonicalStringify } from '../json-canonicalize'

/**
 * Produces the field-level diff consumed by `VersionDiffModal` (task 9.3).
 * Pure, FE-only, dep-free (only the canonicalize helper from
 * `modules/shared/`). Promotion to `@kfolks/core-utils` is a follow-up if a
 * second consumer appears (per design Q3).
 *
 * Structure-aware (not generic JSON): we know the three top-level shapes
 * (`FormSchema`, `WorkflowConfig`, `summaryTemplate`-as-string) and their
 * meaningful identity keys (`fields[].id`, `steps[].id`), so the diff
 * surfaces semantic changes — added/removed/reordered + per-property — not
 * positional noise.
 *
 * Depth bound: properties on `FormField` and `WorkflowStepConfig` are
 * compared via canonicalized-JSON equality, with the canonicalize helper
 * itself bounded at 8 nesting levels (see `canonicalize`'s `MAX_DEPTH`).
 *
 * v1 row (no previous version): callers pass `previous: null` and the diff
 * marks every section as fully added — left side is empty.
 */

export interface DeepDiff {
	formSchema: FormSchemaDiff
	workflowConfig: WorkflowConfigDiff
	summaryTemplate: SummaryTemplateDiff
}

// ─── formSchema ──────────────────────────────────────────────────────────────

export interface FormSchemaDiff {
	addedFields: ReadonlyArray<FormField>
	removedFields: ReadonlyArray<FormField>
	changedFields: ReadonlyArray<FormFieldChange>
}

export interface FormFieldChange {
	id: string
	properties: ReadonlyArray<PropertyChange>
}

export interface PropertyChange {
	property: string
	before: unknown
	after: unknown
}

const FORM_FIELD_COMPARED_PROPERTIES = ['label', 'validation', 'dataSource', 'type'] as const

// ─── workflowConfig ──────────────────────────────────────────────────────────

export interface WorkflowConfigDiff {
	addedSteps: ReadonlyArray<WorkflowStepConfig>
	removedSteps: ReadonlyArray<WorkflowStepConfig>
	changedSteps: ReadonlyArray<WorkflowStepChange>
	reorderedStepIds: ReadonlyArray<string>
}

export interface WorkflowStepChange {
	id: string
	properties: ReadonlyArray<PropertyChange>
}

const WORKFLOW_STEP_COMPARED_PROPERTIES = [
	'specificPeople',
	'groups',
	'submittersManager',
	'collaboratorsManager',
	'skipIfSubmitterIn',
	'slaHours',
	'priorityTransform',
	'label',
	'after',
] as const

// ─── summaryTemplate ─────────────────────────────────────────────────────────

export interface SummaryTemplateDiff {
	before: string | null
	after: string | null
	changed: boolean
}

// ─── public API ──────────────────────────────────────────────────────────────

export function computeDeepDiff(
	current: RequestTypeVersionFullDTO,
	previous: RequestTypeVersionFullDTO | null,
): DeepDiff {
	if (previous === null) return diffAgainstEmpty(current)
	return {
		formSchema: diffFormSchema(current.formSchema, previous.formSchema),
		workflowConfig: diffWorkflowConfig(current.workflowConfig, previous.workflowConfig),
		summaryTemplate: diffSummaryTemplate(current.summaryTemplate, previous.summaryTemplate),
	}
}

function diffAgainstEmpty(current: RequestTypeVersionFullDTO): DeepDiff {
	return {
		formSchema: {
			addedFields: current.formSchema.fields,
			removedFields: [],
			changedFields: [],
		},
		workflowConfig: {
			addedSteps: current.workflowConfig.steps,
			removedSteps: [],
			changedSteps: [],
			reorderedStepIds: [],
		},
		summaryTemplate: {
			before: null,
			after: current.summaryTemplate,
			changed: current.summaryTemplate !== null,
		},
	}
}

// ─── formSchema diff ─────────────────────────────────────────────────────────

function diffFormSchema(current: FormSchema, previous: FormSchema): FormSchemaDiff {
	const previousById = new Map(previous.fields.map((f) => [f.id, f]))
	const currentById = new Map(current.fields.map((f) => [f.id, f]))

	const addedFields = current.fields.filter((f) => !previousById.has(f.id))
	const removedFields = previous.fields.filter((f) => !currentById.has(f.id))

	const changedFields: FormFieldChange[] = []
	for (const curr of current.fields) {
		const prev = previousById.get(curr.id)
		if (!prev) continue
		const properties = comparePropertyList(prev, curr, FORM_FIELD_COMPARED_PROPERTIES)
		if (properties.length > 0) changedFields.push({ id: curr.id, properties })
	}

	return { addedFields, removedFields, changedFields }
}

// ─── workflowConfig diff ─────────────────────────────────────────────────────

function diffWorkflowConfig(current: WorkflowConfig, previous: WorkflowConfig): WorkflowConfigDiff {
	const previousById = new Map(previous.steps.map((s) => [s.id, s]))
	const currentById = new Map(current.steps.map((s) => [s.id, s]))

	const addedSteps = current.steps.filter((s) => !previousById.has(s.id))
	const removedSteps = previous.steps.filter((s) => !currentById.has(s.id))

	const changedSteps: WorkflowStepChange[] = []
	for (const curr of current.steps) {
		const prev = previousById.get(curr.id)
		if (!prev) continue
		const properties = comparePropertyList(prev, curr, WORKFLOW_STEP_COMPARED_PROPERTIES)
		if (properties.length > 0) changedSteps.push({ id: curr.id, properties })
	}

	const reorderedStepIds = computeReorderedStepIds(current.steps, previous.steps)

	return { addedSteps, removedSteps, changedSteps, reorderedStepIds }
}

function computeReorderedStepIds(
	current: ReadonlyArray<WorkflowStepConfig>,
	previous: ReadonlyArray<WorkflowStepConfig>,
): ReadonlyArray<string> {
	if (current.length !== previous.length) return []
	const currIds = current.map((s) => s.id)
	const prevIds = previous.map((s) => s.id)
	if (currIds.join('|') === prevIds.join('|')) return []
	const setEquals = currIds.length === prevIds.length && currIds.every((id) => prevIds.includes(id))
	return setEquals ? currIds : []
}

// ─── summaryTemplate diff ────────────────────────────────────────────────────

function diffSummaryTemplate(current: string | null, previous: string | null): SummaryTemplateDiff {
	return { before: previous, after: current, changed: current !== previous }
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function comparePropertyList(prev: object, curr: object, properties: ReadonlyArray<string>): PropertyChange[] {
	const prevRecord = prev as Record<string, unknown>
	const currRecord = curr as Record<string, unknown>
	const changes: PropertyChange[] = []
	for (const property of properties) {
		const before = prevRecord[property]
		const after = currRecord[property]
		if (canonicalStringify(before) !== canonicalStringify(after)) {
			changes.push({ property, before, after })
		}
	}
	return changes
}
